import { useCallback } from 'react';
import { Form, Field, Dropdown, useFormState } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import {
  FacilityField,
  TerminalField,
  MaterialField,
  SupplierField,
  ReferenceIdField,
  DateOfDischargeField,
  VesselNameField,
} from '@/components/common';
import { FACILITY_KINDS, parseFacilityValue } from '@/config/formOptions';
import { declarationValidation } from '../../declarationsValidation';

const FACILITY_CLEARS = ['terminalId', 'materialId'];

// Terminal shows only for a site — a back-to-back account has none; Figma marks it mandatory.
const DeclarationTerminalField = ({ sites, locked }) => {
  const { values } = useFormState();
  const facility = parseFacilityValue(values.facilityId);
  if (facility.kind !== FACILITY_KINDS.site) return null;
  return <TerminalField sites={sites} required disabled={locked} />;
};

/**
 * Props:
 *   formOptions  useValidateDeclarationFormOptions() result
 *   setValue     (name, value) => void — for the Reference ID Generate button
 *   locked       a linked shipment set Received at/Material/Supplier — disables fields
 */
export const BasicInformationSection = ({ formOptions, setValue, locked = false }) => {
  const t = useTranslations();

  const handleGenerateId = useCallback((number) => setValue('idNumber', number), [setValue]);

  return (
    <Form.Section title={t('SECTION_BASIC_INFORMATION')}>
      <Form.Row columns={2}>
        <ReferenceIdField
          label={t('DECLARATIONS_COL_REFERENCE_ID')}
          prefixLabel={t('DECLARATIONS_COL_REFERENCE_ID')}
          numberLabel={t('DECLARATIONS_COL_REFERENCE_ID')}
          rules={declarationValidation.idNumber}
          onGenerate={handleGenerateId}
        />
        <FacilityField
          options={formOptions.facilityOptions}
          clears={FACILITY_CLEARS}
          disabled={locked}
        />
      </Form.Row>

      <Form.Row columns={2}>
        <DeclarationTerminalField sites={formOptions.sites} locked={locked} />
        <MaterialField
          materials={formOptions.materials}
          certificateType={formOptions.certificateType}
          disabled={locked}
        />
      </Form.Row>

      <Form.Row columns={2}>
        <MultisiteTransferField options={formOptions.multisiteTransferOptions} />
        <SupplierField options={formOptions.supplierOptions} disabled={locked} />
      </Form.Row>

      <Form.Row columns={2}>
        <DateOfDischargeField required />

        <VesselNameField fieldName="vessel" />
      </Form.Row>
    </Form.Section>
  );
};

/**
 * Props:
 *   options  the Yes/No pair this endpoint accepts
 */
// Not required either: unanswerable by `declaration(id)`, and omitted from the save body.

// TODO: enable once the multi-site-transfer branch is built, as in the other forms.
const MultisiteTransferField = ({ options }) => {
  const t = useTranslations();

  return (
    <Field name="multisiteTransfer">
      {({ id, name, value, onChange, onBlur, status, message }) => (
        <Dropdown
          disabled
          id={id}
          name={name}
          label={t('FORM_MULTI_SITE_TRANSFER')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
          options={options}
        />
      )}
    </Field>
  );
};
